import { FC } from 'react';
import { Stack, Button, Group, Text, Image, Paper } from '@mantine/core';
import { IconCheck, IconRotate } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import classes from './DocumentScanner.module.css';

interface ScanPreviewProps {
  processedImage: string | null;
  onKeep: () => void;
  onRetake: () => void;
  isProcessing?: boolean;
}

export const ScanPreview: FC<ScanPreviewProps> = ({
  processedImage,
  onKeep,
  onRetake,
  isProcessing = false,
}) => {
  const { t } = useTranslation(['common']);

  return (
    <Stack gap="md" align="center">
      {/* Title */}
      <Text size="lg" fw={500}>
        {t('documentScanner.reviewScan', {
          defaultValue: 'Review Scan',
        })}
      </Text>

      {/* Single processed image */}
      {processedImage && (
        <Paper withBorder radius="md" p={0} className={classes.previewPaper}>
          <Image
            src={processedImage}
            alt="Scanned document"
            fit="contain"
            className={classes.previewImage}
          />
        </Paper>
      )}

      {/* Instructions */}
      <Text size="sm" c="dimmed" ta="center">
        {t('documentScanner.previewInstructions', {
          defaultValue:
            'Review your scan. Keep if it looks good, or retake if needed.',
        })}
      </Text>

      {/* Action buttons - simplified to just Retake and Keep */}
      <Group gap="md" justify="center" className={classes.previewActions}>
        <Button
          variant="outline"
          leftSection={<IconRotate size={18} />}
          onClick={onRetake}
          disabled={isProcessing}
        >
          {t('documentScanner.retake', {
            defaultValue: 'Retake',
          })}
        </Button>

        <Button
          leftSection={<IconCheck size={18} />}
          onClick={onKeep}
          loading={isProcessing}
        >
          {t('documentScanner.keepPage', {
            defaultValue: 'Keep',
          })}
        </Button>
      </Group>
    </Stack>
  );
};
